import { useRenderedBootHumanWait } from '#~/routes/boot-overlay/boot-budget-view.js';
/* eslint-disable @typescript-eslint/no-shadow */
import * as Wizard from '#~/agent/components/wizard/index.js';
import { fetchAgent } from '#~/api/agent/index.js';
import { useAuth0 } from '#~/auth/index.js';
import { NotFoundError } from '#~/error/index.js';
import { Paths } from '#~/routes/index.js';
import { Success } from './success.jsx';
import { Loader } from '@studio/design-system/loader';
import { unwrap } from '@studio/js-utils/async';
import { castId, normalizeId } from '@studio/models';
import type { Agent } from '@studio/models/agent';
import type { Room } from '@studio/models/room';
import { useAsyncEffect } from '@studio/react-utils/hooks';
import { useState } from 'react';
import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/** `<Complete>` component. */
export const Complete = (): ReactElement => {
  const params = useParams();

  const { roomId } = params;
  const agentId = normalizeId<Agent['id']>(params.agentId);

  if (!roomId || !agentId) {
    throw new NotFoundError();
  }

  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const [agent, setAgent] = useState<Omit<Agent, 'lastShutDownAt' | 'status'>>();

  useAsyncEffect(
    function* generator(_signal: AbortSignal) {
      const { brandId, embedded, iconUrl, modelId, name, roomId, version } = yield* (
        unwrap(fetchAgent(agentId, yield* unwrap(getAccessTokenSilently()))) ?? {}
      );

      setAgent({ brandId, embedded, iconUrl, id: agentId, modelId, name, roomId, version });
    },
    [agentId]
  );

  useRenderedBootHumanWait(Boolean(agent));

  if (!agent) {
    return <Loader />;
  }

  return (
    <Wizard.Root className='m-0 gap-0' currentStep={3} theme='success' totalSteps={3}>
      <div className='flex w-full flex-1 flex-col items-center justify-center border-b-[1px] border-white'>
        <Success />
      </div>

      <div className='flex flex-1 flex-col items-center justify-center'>
        <Wizard.Verbiage
          className='mx-32 mt-0 whitespace-pre-line'
          message="Now, let's get you into the studio."
          title={`Bingo\nYour ${agent.embedded ? 'camera' : 'machine'} has been linked!`}
        />

        <Wizard.ButtonBox>
          <Wizard.Button
            onClick={(): void => {
              navigate(Paths.room.resolve(castId<Room['id']>(roomId)));
            }}
          >
            Launch Studio
          </Wizard.Button>
        </Wizard.ButtonBox>
      </div>
    </Wizard.Root>
  );
};
